import React from 'react';
import { useState, useEffect } from 'react';
import { Typography, Container, Grid, Chip, Menu, MenuItem, Divider, IconButton, TextField } from '@material-ui/core';
import { Label, MoreVert } from '@material-ui/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import useErmaStyling from '../../../../hooks/useErmaStyling';
import LoadingIndicator from '../../../utils/LoadingIndicator';
import { hasUserRole } from '../../../../utils/AuthUtils';
import ServiceAPI from '../../../../service/ServiceAPI';
import Constants from '../../../../utils/Constants';
import { CompletionRecordStatus } from '../../../../enums/InventoryEnum';
import EditDialog from './FragmentEditDialog';
import DeleteConfirmDialog from '../../../utils/FragmentDeleteConfirmDialog';
import ErmaResponsiveRecordGrid from '../../../utils/ErmaResponsiveRecordGrid';
import ErmaResponsiveGridItem from '../../../utils/ErmaResponsiveGridItem';

const ManageCompletion = function(props) {
  const classes = useErmaStyling().ermaStyles;
  const isMobile = useErmaStyling().isMobile;

  const isInventoryManager = hasUserRole(Constants.UserRole.InventoryManager);

  const [showLoading, setShowLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [records, setRecords] = useState(null);
  const [filteredRecords, setFilteredRecords] = useState(null);
  const [searchValue, setSearchValue] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);

  /**
   * 
   */
  useEffect(() => {
    ServiceAPI.InventoryManage().getListing().then((data) => {
      setRecords(data);
      setShowLoading(false);
    });
  }, []);


  /**
   * Handles the opening of the action menu for a specific record.
   * @param {*} event 
   * @param {*} hose 
   */
  const handleMenuOpen = (event, record) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(record);
  };

  /**
   * Handles the closing of the action menu and resets the selected record.
   */
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecord(null);
  };


  /**
   * 
   */
  const handleClick_showReport = () => {
    setShowLoading(true);

    ServiceAPI.InventoryManage().getInventoryReport(selectedRecord.getCompletionRecordId()).then( 
      (result) => {
        // Decode the Base64 string into raw binary data
        const base64Data = result.pdfContent;
        const binaryString = atob(base64Data);
        
        // Convert the binary string into a typed byte array (Uint8Array)
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
    
        // Create a native browser Blob with the correct PDF MIME type
        const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        
        // Generate a temporary Object URL linking to the Blob
        const blobUrl = URL.createObjectURL(pdfBlob);
    
        // Open the Object URL in a clean new browser tab/window
        window.open(blobUrl, '_new');

        setShowLoading(false);
      }
    );

    handleMenuClose();
  }

  /**
   * 
   */
  const handleClick_edit = () => {
    setShowEditDialog(true);
  }

  /**
   * 
   */
  const handleClick_delete = (e) => {
    if (e !== null) {
      setShowDeleteConfirm(true);
      return;
    }

    setShowLoading(true);

    ServiceAPI.InventoryManage().deleteCompletionRecord(selectedRecord.getCompletionRecordId()).then(
      (result) => {
        window.location.reload(); // not great, but will work for now.
      }
    );

    handleMenuClose();
  }

  /**
   * 
   */
  const handleClick_finaliseCompletionRecord = () => {
    setShowLoading(true);
    
    ServiceAPI.CompletionRecord().createInventoryCompletionTranscriptRecordReportAndEmail(selectedRecord).then(
      result => window.location.reload() // not great, but will work for now.
    );
    
    handleMenuClose();
  }

  /**
   * 
   */
  const handleChange_search = (e) => {
    let needle = e.target.value;
    let matches = [];

    setSearchValue(needle);

    if (needle.length < 1) {
      setFilteredRecords(null);
      return;
    }

    records.map((record) => {
      let haystack = [
        record.getInventoryName(),
        record.getDescription(),
        record.getCreated(),
        record.getCompletionStatus()
      ]

      let haystackStr = haystack.join(",").toLowerCase();

      if (haystackStr.indexOf(needle.toLowerCase()) > -1) {
        matches.push(record);
      }
    });

    setFilteredRecords(matches);
  }

  /**
   * 
   * @returns 
   */
  const render = () => {
    if (showLoading) {
      return <div><LoadingIndicator isOpen={true} /></div>;
    }

    return (
      <>
        <Container className={classes.container}>
          <Grid container>
            <Grid item xs={12} sm={10} className={classes.gridTable_fieldAlignLeft}>
              <Typography className={classes.sectionHeading}>
                Manage Completion Records
              </Typography>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Search Records" type="search" variant="outlined" size="small" onChange={handleChange_search} fullWidth/>
            </Grid>
          </Grid>
        </Container>

        <Container className={classes.container}>
          <ErmaResponsiveRecordGrid rowKey={ r => r.getCompletionRecordId() } records={(filteredRecords !== null ? filteredRecords : records)} >
            <ErmaResponsiveGridItem 
              label="Inventory" 
              xs={12} 
              sm={5} 
              element={ r => r.getInventoryName() }
              classes={{
                xs: [classes.gridTable_fieldAlignLeft],
                sm: [classes.gridTable_fieldAlignLeft]
              }}
            />
            
            <ErmaResponsiveGridItem 
              label="Description" 
              xs={12} 
              sm={2} 
              element={ r => r.getDescription() }
              classes={{
                xs: [classes.gridTable_fieldAlignLeft],
                sm: [classes.gridTable_fieldAlignLeft]
              }}
            />

            <ErmaResponsiveGridItem 
              label="Created" 
              xs={12} 
              sm={2} 
              element={ r => renderDate(r.getCreated()) }
              classes={{
                xs: [classes.gridTable_fieldAlignLeft],
                sm: [classes.gridTable_fieldAlignCenter]
              }}
            />

            <ErmaResponsiveGridItem 
              label="Status" 
              noXsLabel
              xs={6} 
              sm={2} 
              element={ r => renderStatusPill(r.getCompletionStatus()) }
              classes={{
                xs: [classes.gridTable_fieldAlignCenter],
                sm: [classes.gridTable_fieldAlignCenter]
              }}
            />

            <ErmaResponsiveGridItem 
              label="Actions" 
              noXsLabel
              xs={6} 
              sm={1} 
              element={ r => renderActions(r) }
              classes={{
                xs: [classes.gridTable_fieldAlignCenter],
                sm: [classes.gridTable_fieldAlignCenter]
              }}
            />
          </ErmaResponsiveRecordGrid>


          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            { 
              isInventoryManager &&
              selectedRecord?.getCompletionStatus() === CompletionRecordStatus.INCOMPLETE && 
              <MenuItem onClick={handleClick_finaliseCompletionRecord}>Finalise</MenuItem> 
            }
            { 
              (selectedRecord?.getCompletionStatus() === CompletionRecordStatus.COMPELTE ||
               selectedRecord?.getCompletionStatus() === CompletionRecordStatus.FORCE_COMPLETE) &&
              <MenuItem onClick={handleClick_showReport}>View Report</MenuItem> 
            }
            {
              isInventoryManager && (
                <>
                  <MenuItem onClick={handleClick_edit}>Edit</MenuItem>
                  <Divider />
                  <MenuItem onClick={handleClick_delete} style={{color: "red"}}>Delete</MenuItem>
                </>
              )
            }
          </Menu>

          <EditDialog 
            isOpen={showEditDialog} 
            record={selectedRecord} 
            onClose={() => { handleMenuClose(); setShowEditDialog(false); }}
          />

          <DeleteConfirmDialog
            show={showDeleteConfirm}
            handleDelete={handleClick_delete}
            tearDown={() => { handleMenuClose(); setShowDeleteConfirm(false); }}
          />
        </Container>
      </>
    );
  }

  /**
   * Renders the action buttons for a specific record.
   * @param {*} hose 
   * @returns 
   */
  const renderActions = (record) => {
    return (
      <IconButton size="small" onClick={(e) => handleMenuOpen(e, record)}>
        <MoreVert />
      </IconButton>
    );
  }
  /**
   * 
   * @param {*} date 
   */
  const renderDate = (date) => {
    if (date === null || date === '') {
      return null;
    }

    return moment(date).format('DD/MM/YYYY HH:mm');
  }

  /**
   * 
   * @param {*} startedDate 
   * @param {*} completedDate 
   */
  const renderStatusPill = (status) => {
    let statusText, statusColor = "";

    switch (status) {
      case CompletionRecordStatus.COMPELTE:
        statusText = "Complete";
        statusColor = classes.success;
        break;

      case CompletionRecordStatus.FORCE_COMPLETE:
        statusText = "Force Complete";
        statusColor = classes.warning;
        break;

      case CompletionRecordStatus.INCOMPLETE:
        statusText = "Incomplete";
        statusColor = classes.warning;
        break;

      default:
        statusText = "Not started";
        statusColor = classes.danger;
    }

    return <Chip color="primary" label={statusText} className={statusColor}/>
  }

  return render();
}

export default ManageCompletion;